import React, { useState } from 'react'
import { Button, Popover } from 'antd'
import { DownOutlined, EyeOutlined, ClockCircleOutlined } from '@ant-design/icons'
import { TaskBullet } from './TaskBullet'
import { SnoozePicker } from './SnoozePicker.lazy'
import { PRIORITY_GROUPS } from '../constants/priority-groups'
import type { ProjectGroupItem } from '../utils/group-by-project'
import type { Task } from '../schemas/task'

export type ProjectGroupProps = {
  group: ProjectGroupItem
  defaultOpen?: boolean
  pendingId: string | null
  onPrimary: (task: Task) => void
  onReplanifier: (task: Task, chosenDate: Date) => void
  onIgnore: (task: Task) => void
  onOpenProject?: (projectId: number | null) => void
  onReplanifierAll?: (group: ProjectGroupItem, chosenDate: Date) => void
}

export const ProjectGroup: React.FC<ProjectGroupProps> = ({
  group,
  defaultOpen = false,
  pendingId,
  onPrimary,
  onReplanifier,
  onIgnore,
  onOpenProject,
  onReplanifierAll,
}) => {
  const [open, setOpen] = useState(defaultOpen)
  const [snoozeAllOpen, setSnoozeAllOpen] = useState(false)
  const topMeta = PRIORITY_GROUPS.find((g) => g.id === group.topPriority)
  const dotColor = topMeta?.accentColor ?? '#1677ff'

  return (
    <div
      data-cy={`assistant--project-group_${group.key}`}
      style={{
        margin: '8px 12px',
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 8,
        boxShadow: '0 1px 2px rgba(15,42,74,0.04)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
          color: 'rgba(0,0,0,0.88)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
        }}
        aria-expanded={open}
      >
        <DownOutlined
          style={{
            fontSize: 11,
            color: 'rgba(0,0,0,0.45)',
            transform: open ? 'rotate(0)' : 'rotate(-90deg)',
            transition: 'transform .15s',
          }}
        />
        <span
          style={{ width: 8, height: 8, borderRadius: 8, background: dotColor }}
        />
        <span style={{ fontSize: 13, fontWeight: 600 }}>{group.client}</span>
        {group.projectId != null && (
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: 11,
              color: 'rgba(0,0,0,0.45)',
            }}
          >
            #{group.projectId}
          </span>
        )}
        {group.projectType && (
          <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)' }}>
            · {group.projectType}
          </span>
        )}
        <span
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {group.urgentCount > 0 && (
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                color: '#cf1322',
                background: '#fff1f0',
                border: '1px solid #ffccc7',
                padding: '0 5px',
                borderRadius: 3,
              }}
            >
              {group.urgentCount} urgent{group.urgentCount > 1 ? 's' : ''}
            </span>
          )}
          <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)' }}>
            {group.items.length}
          </span>
        </span>
      </button>
      {open && (
        <div>
          <div style={{ padding: '4px 4px 8px' }}>
            {group.items.map((t) => (
              <TaskBullet
                key={t.id}
                task={t}
                isPending={pendingId === t.id}
                onPrimary={onPrimary}
                onReplanifier={onReplanifier}
                onIgnore={onIgnore}
              />
            ))}
          </div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              padding: '8px 12px 10px',
              borderTop: '1px solid rgba(0,0,0,0.04)',
            }}
          >
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onOpenProject?.(group.projectId)}
              data-cy={`action--assistant_open-project_${group.key}`}
              style={{ flex: 1 }}
              disabled={group.projectId == null}
            >
              Ouvrir le dossier
            </Button>
            <Popover
              open={snoozeAllOpen}
              onOpenChange={(v) => onReplanifierAll && setSnoozeAllOpen(v)}
              trigger="click"
              placement="topRight"
              destroyOnHidden
              content={
                <SnoozePicker
                  onPick={(date) => {
                    setSnoozeAllOpen(false)
                    onReplanifierAll?.(group, date)
                  }}
                  onCancel={() => setSnoozeAllOpen(false)}
                />
              }
            >
              <Button
                size="small"
                icon={<ClockCircleOutlined />}
                data-cy={`action--assistant_replanifier-all_${group.key}`}
                style={{ flex: 1 }}
                disabled={!onReplanifierAll}
              >
                Tout replanifier
              </Button>
            </Popover>
          </div>
        </div>
      )}
    </div>
  )
}
